import { useCallback } from "react";
import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { AccrualImporterRow } from "../types";
import { propagateSharedField } from "../useAccrualImporterState";
import {
    withDerivedAccrualLineAmount,
    isAmountDrivingAccrualField,
} from "../accrualImporterLineAmount.helpers";
import {
    applySpreadsheetColumnUpdate,
    isAccrualImporterRowKey,
} from "./applySpreadsheetColumnUpdate";

export function useAccrualImporterSubsetChangeHandler({
    setRows,
    columns,
    globalRowIndices,
}: {
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    columns: ColumnDef[];
    globalRowIndices: number[];
}) {
    return useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            setRows((prevRows) => {
                const nextRows = [...prevRows];

                for (const [localRowIdx, colIdx] of changedCells) {
                    const globalRowIdx = globalRowIndices[localRowIdx];
                    const col = columns[colIdx];
                    const incomingRow = newData[localRowIdx];

                    if (
                        globalRowIdx === undefined ||
                        !col ||
                        !incomingRow ||
                        globalRowIdx >= nextRows.length ||
                        col.field === "_rowNumber" ||
                        !isAccrualImporterRowKey(col.field)
                    ) {
                        continue;
                    }

                    nextRows[globalRowIdx] = applySpreadsheetColumnUpdate({
                        row: nextRows[globalRowIdx],
                        columnField: col.field,
                        incoming: incomingRow,
                    });

                    if (isAmountDrivingAccrualField(col.field)) {
                        nextRows[globalRowIdx] = withDerivedAccrualLineAmount(
                            nextRows[globalRowIdx],
                        );
                    }

                    propagateSharedField({
                        rows: nextRows,
                        prevRows,
                        rowIndex: globalRowIdx,
                        field: col.field,
                    });
                }

                return nextRows;
            });
        },
        [columns, globalRowIndices, setRows],
    );
}
